import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'clsx';
import { Button } from '@repo/design-system-kit';
import { TNCLink } from '@repo/shared-kit/components';
import { IconArrowLeftOutlined } from '@repo/monochrome-icons';
import { ConsentCheckbox } from '../consent-checkbox';
import styles from './consent-page.module.css';

export interface ConsentPageProps {
  onContinue: (hasConsent: boolean) => void;
  onBack?: () => void;
  className?: string;
  buttonStretch?: boolean;
}

export function ConsentPage({
  onContinue,
  onBack,
  className,
  buttonStretch = true,
}: ConsentPageProps): React.ReactElement {
  const [hasConsent, setHasConsent] = useState<boolean>(false);

  const handleConsentChange = (isChecked: boolean): void => {
    setHasConsent(isChecked);
  };

  const handleContinue = (): void => {
    onContinue(hasConsent);
  };

  const handleBack = (): void => {
    onBack?.();
  };

  return (
    <div className={cx(styles.content, className)}>
      <h1 className="heading-section mb-32">
        <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.title" />
      </h1>

      <div className={styles.consentSection}>
        <ConsentCheckbox onChange={handleConsentChange}>
          <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.caption" />
        </ConsentCheckbox>
      </div>

      <div>
        <Button
          className="mb-16"
          data-test-consent-continue
          onClick={handleContinue}
          size="medium"
          stretch={buttonStretch}
          variant="primary"
        >
          <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.continue" />
        </Button>
      </div>

      <div className={styles.additionalInfo}>
        <span className="caption">
          <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.footer-text" />
          <TNCLink className="caption-link" language="en" tncKey="qonto-privacy-policy">
            <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.link-text" />
          </TNCLink>
          <FormattedMessage id="account-management-kit.sign_up.gdpr-consent.footer-end" />
        </span>
      </div>

      {onBack ? (
        <div className={styles.backButtonContainer}>
          <Button
            className={styles.backButton}
            onClick={handleBack}
            size="small"
            type="button"
            variant="tertiary"
          >
            <div className={styles.iconContainer}>
              <IconArrowLeftOutlined />
              <FormattedMessage id="account-management-kit.btn.back" />
            </div>
          </Button>
        </div>
      ) : null}
    </div>
  );
}
